import React, { useEffect } from "react";
import { CheckCircle2, X, Sparkles } from "lucide-react";

/**
 * Confirmation modal shown once when a client submits their questionnaire for the FIRST time.
 * Rich, on-brand and celebratory — reduces anxiety ("Ma demande est-elle passée ?") and
 * signals the next step ("Recontact sous 48 h").
 */
export default function QuestionnaireConfirmationModal({ open, onClose, isWedding = true }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (!open) return null;
  const ctxWord = isWedding ? "votre mariage" : "votre événement";

  return (
    <div
      data-testid="questionnaire-confirmation-modal"
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-in fade-in duration-300"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="relative max-w-lg w-full bg-gradient-to-b from-[#0F0C08] to-black border border-[#DCB457]/50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sparkle top */}
        <div
          className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#DCB457] to-transparent"
          aria-hidden
        />

        <button
          type="button"
          onClick={onClose}
          data-testid="qcm-close"
          className="absolute top-4 right-4 text-white/40 hover:text-white transition"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>

        <div className="px-8 pt-12 pb-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto rounded-full bg-[#DCB457]/10 border border-[#DCB457]/40 flex items-center justify-center mb-6 relative">
            <CheckCircle2 size={36} strokeWidth={1.5} className="text-[#DCB457]" />
            <Sparkles
              size={14}
              className="absolute -top-1 -right-1 text-[#DCB457] animate-pulse"
            />
          </div>

          <p className="text-[10px] uppercase tracking-[0.28em] text-[#DCB457] mb-4">Merci pour votre confiance</p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl leading-tight text-white">
            Questionnaire{" "}
            <span className="italic gold-text">bien reçu.</span>
          </h2>

          <p className="text-white/70 text-[15px] leading-relaxed mt-5 max-w-md mx-auto">
            Un immense merci d&apos;avoir pris le temps de renseigner votre questionnaire pour{" "}
            <b className="text-white">{ctxWord}</b>. Chaque détail nous permet de composer une soirée à votre image.
          </p>

          <div
            className="mt-7 p-5 border-l-2 border-[#DCB457] bg-[#DCB457]/[0.06] text-left"
            data-testid="qcm-next-step"
          >
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457] mb-2">Prochaine étape</p>
            <p className="text-white/85 text-sm leading-relaxed">
              Notre équipe étudie vos réponses et vous <b className="text-white">recontacte sous 48 h ouvrées</b> pour
              finaliser ensemble l&apos;ambiance, le déroulé et les moments clés.
            </p>
          </div>

          <p className="text-white/45 text-xs mt-6">
            Un email de confirmation vient également d&apos;être envoyé. Vous pouvez modifier vos réponses à tout moment.
          </p>

          <button
            type="button"
            onClick={onClose}
            data-testid="qcm-confirm-btn"
            className="btn-gold mt-8 !px-10"
          >
            Continuer
          </button>
        </div>

        <div
          className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-[#DCB457]/50 to-transparent"
          aria-hidden
        />
      </div>
    </div>
  );
}


/**
 * Badge "✓ Complété" — persistent tag shown on questionnaire pages once submitted.
 */
export function CompletedBadge({ label = "Complété" }) {
  return (
    <span
      data-testid="questionnaire-completed-badge"
      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-300 border border-emerald-400/50 bg-emerald-500/[0.08] px-3 py-1.5"
    >
      <CheckCircle2 size={12} strokeWidth={1.8} className="text-emerald-300" /> {label}
    </span>
  );
}
